import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTracking from 'hooks/useTracking';
import useTranslate from 'hooks/useTranslate';
import useGoogleTagManagerTrack from 'hooks/useGoogleTagManagerTrack';
import { AuthenticateGrantType, AuthenticateProvider } from 'constants/auth';
import { GoogleTagManagerEvent } from 'constants/google';
import { CODE_LOGIN, LOGIN_WITHOUT_EMAIL_URL } from 'constants/routes';
import { getCurrentUser } from 'data/api';
import { authenticateUser } from 'data/api/account';
import { ResponseCode } from 'data/api/response-codes';
import { authenticateFailEvent, authenticateSuccessEvent } from 'libs/common/event-tracker/events';
import { getFingerprint } from 'libs/utils/fingerprint';
import { urlWithParams } from 'libs/utils/url';
import { navigateToPage } from 'libs/utils/window';
import { actions } from 'state/auth/slice';
import { AuthExternalRegisterView } from 'state/auth/constants';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import GoogleOauthButton from '../GoogleOauthButton';
import useSuccessUrl from '../hooks/useSuccessUrl';
import { jsx as _jsx } from "react/jsx-runtime";

const Google = _ref => {
  let {
    setError
  } = _ref;
  const {
    track
  } = useTracking();
  const translate = useTranslate('auth.select_type');
  const dispatch = useDispatch();
  const {
    googleTagManagerTrack
  } = useGoogleTagManagerTrack();
  const isLoginWithoutEmailInModalEnabled = useSelector(getIsFeatureSwitchEnabled('login_without_email_in_modal'));
  const successUrl = useSuccessUrl();

  function handleLoginWithoutEmail(controlCode, email) {
    if (isLoginWithoutEmailInModalEnabled) {
      dispatch(actions.setExternalRegisterData({
        view: AuthExternalRegisterView.PasswordVerification,
        data: {
          idToken: controlCode,
          email
        }
      }));
      return;
    }

    navigateToPage(urlWithParams(LOGIN_WITHOUT_EMAIL_URL, {
      control_code: controlCode
    }));
  }

  const handleSuccess = async _ref2 => {
    let {
      token,
      fields: {
        email,
        name
      },
      redirectUrl
    } = _ref2;
    const fingerprint = await getFingerprint();
    const response = await authenticateUser({
      grantType: AuthenticateGrantType.Assertion,
      provider: AuthenticateProvider.Google,
      assertion: token,
      fingerprint
    });

    if ('errors' in response) {
      var _response$payload;

      // TODO: handle ResponseCode.SessionFromTokenError
      if (response.code === ResponseCode.NotFound) {
        dispatch(actions.setExternalRegisterData({
          view: AuthExternalRegisterView.GoogleRegister,
          data: {
            idToken: token,
            realName: name,
            email
          }
        }));
      } else if (response.code === ResponseCode.LoginWithoutEmail && (_response$payload = response.payload) !== null && _response$payload !== void 0 && _response$payload.control_code) {
        handleLoginWithoutEmail(response.payload.control_code, email);
      } else if (response.code === ResponseCode.Required2FA && response.payload) {
        navigateToPage(CODE_LOGIN({
          refUrl: redirectUrl || successUrl,
          authProps: response.payload
        }));
      } else {
        setError(response.message);
      }

      return;
    }

    const getCurrentUserResp = await getCurrentUser();
    const userId = 'errors' in getCurrentUserResp ? undefined : getCurrentUserResp.user.id;
    googleTagManagerTrack(GoogleTagManagerEvent.Login, {
      auth_type: 'google',
      user_email: email
    });
    track(authenticateSuccessEvent({
      type: 'google',
      userId
    }));
    navigateToPage(redirectUrl || successUrl);
  };

  const handleFailure = useCallback(() => {
    setError(translate('social_login_error'));
    track(authenticateFailEvent({
      type: 'google',
      error: 'User closed screen or did not provide permissions'
    }));
  }, [track, setError, translate]);
  return /*#__PURE__*/_jsx(GoogleOauthButton, {
    onSuccess: handleSuccess,
    onFailure: handleFailure
  });
};

export default Google;